import { type AxiosCompatibleResponse } from '../http/types';

import { type RepriceProductsRequestBody } from './aliases';
import { type SellerProductsResponse, type StatusFilter } from './types';

import {
  DELETE_PRODUCTS_ENDPOINT,
  RELIST_PRODUCTS,
  REPOP_PRODUCTS,
  REPRICE_PRODUCTS_ENDPOINT,
  REPRICE_PRODUCTS_SUMMARY,
  SELLER_PRODUCTS,
  SELLER_PRODUCTS_LISTED,
  SHOP_BULK_SOLD_PRODUCTS_ENDPOINT,
} from '@/constants/endpoints';
import http, { typedHttp } from '@/modules/http';
import { replacePathParams } from '@/modules/http/replacePathParams';

export function getProductListedCounters() {
  return typedHttp.get(SELLER_PRODUCTS_LISTED, {
    withAuth: true,
  });
}

type GetSellerProducts = {
  cursor?: string;
  lang: string;
  limit?: number;
  showProductInsights?: boolean;
  statusFilter?: StatusFilter;
  searchParams: {
    what?: string;
    categories?: string;
    brands?: string;
  };
};

export function getSellerProducts({
  cursor = '',
  lang,
  limit = 24,
  showProductInsights,
  statusFilter,
  searchParams,
}: GetSellerProducts): Promise<
  AxiosCompatibleResponse<SellerProductsResponse>
> {
  return http.get(SELLER_PRODUCTS, {
    withAuth: true,
    params: {
      lang,
      cursor,
      limit,
      statusFilter,
      ...searchParams,
      showProductInsights,
    },
  });
}

export function bulkDeleteProducts(
  productIds: number[]
): Promise<AxiosCompatibleResponse<void>> {
  return http.delete(DELETE_PRODUCTS_ENDPOINT, {
    data: { productIds },
    withAuth: true,
  });
}

export function bulkMarkSoldProducts(
  productIds: number[]
): Promise<AxiosCompatibleResponse<void>> {
  return typedHttp.put(
    replacePathParams(SHOP_BULK_SOLD_PRODUCTS_ENDPOINT, {
      productIds: productIds.join(','),
    }),
    null,
    {
      withAuth: true,
    }
  );
}

export function getRelistProducts({
  after = '',
  limit = 24,
}: {
  after?: string;
  limit?: number;
}) {
  return typedHttp.get(RELIST_PRODUCTS, {
    withAuth: true,
    params: {
      after,
      limit,
    },
  });
}

export function getRepriceProductsSummary(productIds: number[]) {
  return typedHttp.get(
    replacePathParams(REPRICE_PRODUCTS_SUMMARY, {
      productIds: productIds.join(','),
    })
  );
}

export function repriceProducts(
  products: RepriceProductsRequestBody['products']
) {
  return typedHttp.post(
    REPRICE_PRODUCTS_ENDPOINT,
    { products },
    {
      withAuth: true,
    }
  );
}

export function getRepopProducts({ limit }: { limit?: number }) {
  return typedHttp.get(REPOP_PRODUCTS, {
    params: { limit },
    withAuth: true,
  });
}
